import React, { useState } from 'react';
import ServiceLayout from '../components/ServiceLayout.jsx';
import { 
  CheckCircle2, HelpCircle, Info, AlertCircle, User, ChevronDown, 
  Quote, ArrowRight, Plus, X, Calendar, Phone, Heart, Users, 
  MessageCircle, ShieldAlert, Sparkles, FileText, Compass, MapPin, Clock, Award
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Helmet } from 'react-helmet-async';

// Custom designed, perfectly transparent SVG Red Wax Seal
const RedWaxSeal = ({ className = "w-24 h-24" }) => (
  <img 
    src="https://assets.auxodev.dk/assets/c5965315-775d-45c8-ad9e-70e7ef29846e/corehypnose-lak-segl.png" 
    alt="Core Kvalitetsstempel" 
    className={`${className} object-contain`}
  />
);

const AccordionItem = ({ title, children, isOpen, onClick }) => {
  return (
    <div className="bg-white rounded-2xl border border-[#E8DDCE] overflow-hidden shadow-sm h-full flex flex-col">
      <button
        onClick={onClick}
        className="flex w-full items-center justify-between p-6 text-left hover:bg-[#F4EFE7]/30 transition-colors group cursor-pointer"
      >
        <span className="text-xl font-serif text-[#123F13] flex items-center gap-3">
          <HelpCircle className="text-[#8C2F12] w-6 h-6 shrink-0" /> {title}
        </span>
        <div className="text-[#3B352F]/40 transition-transform duration-300">
          {isOpen ? <X size={20} /> : <Plus size={20} />}
        </div>
      </button>
      <AnimatePresence initial={false}>
        {isOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: 'easeInOut' }}
            className="overflow-hidden flex-1 flex flex-col"
          >
            <div className="p-6 pt-0 border-t border-[#E8DDCE]/50 flex-1">
              <div className="pt-4 text-[#3B352F]/80 leading-relaxed">
                {children}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const Parterapi = () => {
  const [openItems, setOpenItems] = useState([]);

  const toggleItem = (value) => {
    setOpenItems(prev => 
      prev.includes(value) 
        ? prev.filter(item => item !== value)
        : [...prev, value]
    );
  };

  const treatmentSteps = [
    "Afklaring: Vi starter med at forstå, hvad I søger hjælp til, hvad der udløser problemerne, og hvad I hver især håber kan blive anderledes.",
    "Kortlægning af mønstre: Vi ser på konfliktcirklen: hvad der sker før, under og efter en konflikt, og hvilke strategier der fastholder afstanden.",
    "Nye samtalegreb: I får konkrete greb til at tale kortere, roligere og mere præcist, så samtalen ikke bliver overtaget af gamle reaktioner.",
    "Plan for næste skridt: Efter den første afklaring får I et forslag til strategi, forløb og næste skridt. Det kan være få sessioner eller et mere sammenhængende forløb."
  ];

  return (
    <ServiceLayout 
      title={<>Når I vil forstå <br className="hidden md:inline" /> hinanden igen</>}
      subtitle="Hjælp til kommunikation, konflikter, afstand, usikkerhed og gentagne mønstre i parforholdet."
      categoryLabel="Parterapi og relationscoaching i Valby"
      metaTitle="Parterapi i Valby | Relationscoaching | Core Hypnose"
      metaDescription="Parterapi og relationscoaching i Valby for dig eller jer, der ønsker hjælp til kommunikation, konflikter, afstand og gentagne mønstre. Første relationssession fra 375 kr. pr. person."
      description="Jeg hjælper jer med at få overblik over de mønstre, der skaber afstand, skænderier, tavshed eller utryghed i relationen. Parterapi handler ikke om at finde en skyldig. Det handler om at skabe mere ro, tydelighed og kontakt, så I kan tale sammen på en ny måde."
      image="https://assets.auxodev.dk/cdn-cgi/image/width=1200,quality=85,format=auto/assets/c5965315-775d-45c8-ad9e-70e7ef29846e/parterapi-og-relations-coaching.png"
      showContentSection={false}
      actions={
        <>
          <Link to="/bestil-tid" className="inline-flex items-center justify-center gap-1.5 sm:gap-2 bg-[#123F13] text-white px-3 py-2.5 sm:px-5 sm:py-3 rounded-lg font-bold text-xs sm:text-sm md:text-base hover:bg-[#1a5a1b] transition-all shadow-md shrink-0">
            <Calendar size={16} className="sm:w-[18px] sm:h-[18px]" /> Book første relationssession
          </Link>
          <a href="#priser" className="inline-flex items-center justify-center gap-1.5 sm:gap-2 border-2 border-[#123F13] text-[#123F13] px-3 py-2.5 sm:px-5 sm:py-3 rounded-lg font-bold text-xs sm:text-sm md:text-base hover:bg-[#f3f4f6] transition-all shrink-0">
            Se priser og rammer
          </a>
        </>
      }
    >
      {/* Hero Note */}
      <div className="bg-white rounded-2xl border border-[#E8DDCE] p-6 shadow-sm mb-16 flex items-start gap-4 mt-8">
        <div className="bg-[#FAF7F2] p-2 rounded-full text-[#8C2F12] shrink-0">
          <CheckCircle2 size={20} />
        </div>
        <div>
          <strong className="text-[#123F13] block mb-1 text-lg">Du behøver ikke vide præcis, hvad I har brug for.</strong>
          <span className="text-[#3B352F]/70 leading-relaxed">
            Du kan komme alene eller sammen med din partner. Den første relationssession handler om at skabe overblik og finde et konkret næste skridt.
          </span>
        </div>
      </div>

      {/* Stats Section */}
      <section className="mb-20" aria-label="Praktisk overblik">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          <div className="bg-[#FAF7F2] border border-[#E8DDCE] rounded-2xl p-6 shadow-sm hover:shadow-md transition-all">
            <div className="text-[#8C2F12] mb-3"><MapPin size={24} /></div>
            <strong className="text-xl font-serif text-[#123F13] block mb-2">Valby</strong>
            <span className="text-[#3B352F]/70 text-sm leading-relaxed block">Fysiske sessioner på Valby Tingsted 7 eller online, hvis det passer bedre.</span>
          </div>
          <div className="bg-[#FAF7F2] border border-[#E8DDCE] rounded-2xl p-6 shadow-sm hover:shadow-md transition-all">
            <div className="text-[#8C2F12] mb-3"><Clock size={24} /></div>
            <strong className="text-xl font-serif text-[#123F13] block mb-2">50 min.</strong>
            <span className="text-[#3B352F]/70 text-sm leading-relaxed block">En rolig og fokuseret session med plads til afklaring og konkrete næste skridt.</span>
          </div>
          <div className="bg-[#FAF7F2] border border-[#E8DDCE] rounded-2xl p-6 shadow-sm hover:shadow-md transition-all">
            <div className="text-[#8C2F12] mb-3"><Award size={24} /></div>
            <strong className="text-xl font-serif text-[#123F13] block mb-2">375 kr.</strong>
            <span className="text-[#3B352F]/70 text-sm leading-relaxed block">Første relationssession pr. person. Én person 375 kr. · to personer 750 kr.</span>
          </div>
          <div className="bg-[#FAF7F2] border border-[#E8DDCE] rounded-2xl p-6 shadow-sm hover:shadow-md transition-all">
            <div className="text-[#8C2F12] mb-3"><Users size={24} /></div>
            <strong className="text-xl font-serif text-[#123F13] block mb-2">3 pr. uge</strong>
            <span className="text-[#3B352F]/70 text-sm leading-relaxed block">Kun 3 introduktionssessioner frigives hver uge, så der er tid til ordentlig afklaring.</span>
          </div>
        </div>
      </section>

      {/* Når relationen er blevet svær */}
      <section className="mb-20">
        <div className="max-w-3xl mb-12">
          <span className="text-[#8C2F12] font-bold text-xs uppercase tracking-widest flex items-center gap-2 mb-3">
            <span className="w-8 h-[1px] bg-[#8C2F12]"></span>
            Når relationen er blevet svær
          </span>
          <h2 className="text-3xl md:text-4xl font-serif text-[#123F13] mb-6">Parterapi er ikke kun for par, der<br className="hidden md:inline" /> er tæt på at gå fra hinanden</h2>
          <p className="text-lg text-[#3B352F]/70 leading-relaxed">
            Mange søger hjælp, når de samme konflikter bliver ved med at gentage sig. Andre kommer, fordi der er blevet stille, koldt eller uklart mellem dem.
            Uanset om I er i krise, i tvivl eller bare ønsker en bedre måde at være sammen på, kan et struktureret forløb skabe mere overblik.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[
            { title: "I skændes om det samme igen og igen", desc: "Samtalerne starter forskelligt, men ender i den samme fastlåste position." },
            { title: "Én trækker sig, og én presser på", desc: "Jo mere den ene vil tale, desto mere lukker den anden ned." },
            { title: "Der er afstand, tavshed eller kulde", desc: "I fungerer praktisk, men nærhed, tryghed eller lyst er blevet sværere." },
            { title: "Tilliden er blevet ramt", desc: "Utroskab, løgne, brudte aftaler eller gamle konflikter fylder for meget." },
            { title: "Tankemylder fylder i relationen", desc: "Bekymringer, grubleri og analyser tager energi fra kontakten mellem jer." },
            { title: "I skal træffe en vigtig beslutning", desc: "Skal I blive sammen, gå hver til sit eller finde en ny form for relation?" }
          ].map((item, idx) => (
            <div key={idx} className="bg-white border border-[#E8DDCE] rounded-2xl p-6 shadow-sm flex gap-4 items-start">
              <div className="w-2.5 h-2.5 rounded-full bg-[#8C2F12] mt-2.5 shrink-0 shadow-[0_0_0_6px_rgba(140,47,18,0.1)]" />
              <div>
                <h3 className="text-lg font-bold text-[#123F13] mb-2">{item.title}</h3>
                <p className="text-[#3B352F]/70 text-sm leading-relaxed">{item.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Et andet blik på konflikter */}
      <section className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-20">
        <div className="bg-[#123F13] text-white rounded-3xl p-8 lg:p-12 shadow-lg relative overflow-hidden flex flex-col justify-between min-h-[380px]">
          <div className="relative z-10 space-y-6">
            <span className="text-[#E8DDCE] font-bold text-xs uppercase tracking-widest block">Et andet blik på konflikter</span>
            <h2 className="text-3xl md:text-4xl font-serif leading-tight">Problemet er sjældent kun det, I skændes om</h2>
            <p className="text-white/80 leading-relaxed">
              I parforhold kan indholdet skifte: børn, økonomi, praktiske opgaver, sex, svigerfamilie, telefoner, tid eller prioriteringer.
              Men mønstret bag konflikten er ofte det samme.
            </p>
          </div>
          <div className="border-l-4 border-[#8C2F12] pl-6 mt-8 relative z-10 flex justify-between items-end gap-4">
            <p className="font-serif text-2xl italic text-[#E8DDCE] leading-relaxed flex-1">
              "Det afgørende er ikke kun, hvad I taler om. Det er, hvordan I kommer til at tale om det."
            </p>
            <RedWaxSeal className="w-16 h-16 sm:w-20 sm:h-20 rotate-[-6deg] drop-shadow-[0_10px_18px_rgba(0,0,0,0.3)] shrink-0" />
          </div>
        </div>

        <div className="bg-white border border-[#E8DDCE] rounded-3xl p-8 lg:p-12 shadow-sm flex flex-col justify-center space-y-6">
          <h3 className="text-2xl md:text-3xl font-serif text-[#123F13]">Når samtalen bliver en kamp</h3>
          <p className="text-[#3B352F]/80 leading-relaxed">
            Mange par forsøger egentlig at løse problemet. Men hvis samtalen bliver præget af forsvar, bebrejdelser, analyser, tavshed eller gentagelser,
            kan selve måden at tale på komme til at fastholde konflikten.
          </p>
          <p className="text-[#3B352F]/80 leading-relaxed">
            I min tilgang arbejder jeg med at opdage de processer, der holder jer fast. Det kan være bekymring, grubleri, kontrol, tolkninger, undgåelse
            eller et stort behov for at få den anden til at forstå det hele på præcis samme måde.
          </p>
          <p className="text-[#3B352F]/80 leading-relaxed font-medium text-[#123F13]">
            Når I får øje på mønstret, bliver det lettere at stoppe op tidligere og vælge en anden vej.
          </p>
        </div>
      </section>

      {/* Infografik */}
      <section className="bg-[#FAF7F2] border border-[#E8DDCE] rounded-3xl p-8 lg:p-12 shadow-sm mb-20">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12">
          <div>
            <span className="text-[#8C2F12] font-bold text-xs uppercase tracking-widest flex items-center gap-2 mb-2">
              <span className="w-8 h-[1px] bg-[#8C2F12]"></span>
              Infografik
            </span>
            <h2 className="text-3xl md:text-4xl font-serif text-[#123F13]">Fra konfliktcirkel til kontaktcirkel</h2>
          </div>
          <p className="text-[#3B352F]/70 max-w-sm leading-relaxed">
            Et enkelt overblik over, hvad der ofte sker i fastlåste relationer – og hvad I kan øve i stedet.
          </p>
        </div>

        {/* Steps Flow */}
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4 relative mb-12">
          <div className="hidden md:block absolute left-[10%] right-[10%] top-[34px] h-0.5 bg-gradient-to-r from-[#8C2F12]/20 to-[#123F13]/20 z-0" />
          {[
            { step: "1", title: "En situation rammer", desc: "En kommentar, et blik, en aftale eller en tavshed bliver tolket som noget vigtigt." },
            { step: "2", title: "Tankerne tager fart", desc: "“Hun er ligeglad”, “han hører mig aldrig”, “det bliver aldrig anderledes”." },
            { step: "3", title: "Strategien starter", desc: "Forklaring, pres, forsvar, kritik, tilbagetrækning, tavshed eller overanalyse." },
            { step: "4", title: "Afstanden vokser", desc: "I reagerer mere på mønstret end på hinanden. Kontakten bliver sværere." },
            { step: "5", title: "Ny kontakt øves", desc: "I lærer at opdage processen tidligere og tale kortere, klarere og roligere." }
          ].map((item, idx) => (
            <div key={idx} className="bg-white border border-[#E8DDCE] rounded-2xl p-5 shadow-sm relative z-10 flex flex-col justify-between min-h-[180px]">
              <div>
                <div className={`w-9 h-9 rounded-full flex items-center justify-center text-white font-bold mb-4 shadow-md ${idx % 2 === 1 ? 'bg-[#8C2F12]' : 'bg-[#123F13]'}`}>
                  {item.step}
                </div>
                <h4 className="font-bold text-[#123F13] mb-2 leading-tight">{item.title}</h4>
              </div>
              <p className="text-xs text-[#3B352F]/70 leading-relaxed">{item.desc}</p>
            </div>
          ))}
        </div>

        {/* Split Diagram */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-gradient-to-b from-[#8C2F12]/5 to-white border border-[#E8DDCE] rounded-2xl p-6 lg:p-8">
            <h3 className="text-2xl font-serif text-[#123F13] mb-3">Konfliktcirklen</h3>
            <p className="text-sm text-[#3B352F]/70 leading-relaxed mb-6">
              Kendetegnes ofte af gentagelser, gamle sager, forsvar, krav om enighed, tavshed eller samtaler, der bliver længere og mere udmattende.
            </p>
            <div className="flex flex-wrap gap-2">
              {["Grubleri", "Bebrejdelser", "Forsvar", "Tilbagetrækning", "Kontrol"].map((pill, i) => (
                <span key={i} className="bg-white border border-[#E8DDCE] text-[#3B352F]/80 text-xs font-bold px-3 py-1.5 rounded-full shadow-sm">
                  {pill}
                </span>
              ))}
            </div>
          </div>

          <div className="bg-gradient-to-b from-[#123F13]/5 to-white border border-[#E8DDCE] rounded-2xl p-6 lg:p-8">
            <h3 className="text-2xl font-serif text-[#123F13] mb-3">Kontaktcirklen</h3>
            <p className="text-sm text-[#3B352F]/70 leading-relaxed mb-6">
              Kendetegnes af pauser, tydelige behov, mindre tankekamp, mere ansvar for egen reaktion og samtaler, der har et klart formål.
            </p>
            <div className="flex flex-wrap gap-2">
              {["Overblik", "Kortere samtaler", "Tydelige aftaler", "Ro", "Nærvær"].map((pill, i) => (
                <span key={i} className="bg-white border border-[#E8DDCE] text-[#3B352F]/80 text-xs font-bold px-3 py-1.5 rounded-full shadow-sm">
                  {pill}
                </span>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Min tilgang */}
      <section className="grid grid-cols-1 lg:grid-cols-12 gap-8 mb-20">
        <div className="lg:col-span-5 bg-gradient-to-br from-[#123F13] to-[#173F35] text-white rounded-3xl p-8 lg:p-10 shadow-lg relative overflow-hidden flex flex-col justify-between min-h-[420px]">
          <div className="absolute -right-16 -bottom-16 w-64 h-64 rounded-full bg-white/5 border border-white/10" />
          <div className="space-y-6 relative z-10">
            <span className="text-[#E8DDCE] font-bold text-xs uppercase tracking-widest flex items-center gap-2">
              <span className="w-8 h-[1px] bg-[#E8DDCE]"></span>
              Min tilgang
            </span>
            <h2 className="text-3xl font-serif leading-tight">Metakognitivt blik på parterapi</h2>
            <p className="text-white/80 leading-relaxed">
              Fokus er ikke at fjerne alle tanker eller følelser. Fokus er at ændre jeres forhold til dem, så bekymring, grubleri og gamle fortællinger ikke styrer kontakten mellem jer.
            </p>
          </div>
          <div className="relative z-10 flex justify-start mt-8">
            <RedWaxSeal className="w-28 h-28 rotate-[-8deg] drop-shadow-[0_15px_24px_rgba(0,0,0,0.28)]" />
          </div>
        </div>

        <div className="lg:col-span-7 flex flex-col gap-4 justify-between">
          {[
            { step: "1", title: "Jeg hjælper jer med at se mønstret udefra", desc: "Når konflikten bliver kortlagt neutralt, bliver det lettere at tale om det, der sker, uden at samtalen bliver en kamp om skyld." },
            { step: "2", title: "Vi arbejder med tankeprocesser, ikke kun indhold", desc: "Det handler mindre om at analysere hver eneste detalje og mere om at opdage, hvordan bekymring, grubleri og tolkninger får lov til at fylde." },
            { step: "3", title: "I får en mere konkret måde at tale sammen på", desc: "Jeg hjælper jer med at gøre samtalerne kortere, tydeligere og mere brugbare, så I ikke mister hinanden undervejs." },
            { step: "4", title: "Hypnose kan inddrages efter behov", desc: "Hypnose forstås her som fokuseret opmærksomhed, rolig guidning og terapeutiske forslag. Det kan bruges til at støtte ro, nærvær og nye reaktionsmønstre – uden mystik." }
          ].map((item, idx) => (
            <div key={idx} className="bg-white border border-[#E8DDCE] rounded-2xl p-5 shadow-sm flex gap-4 items-start">
              <div className="w-9 h-9 rounded-full bg-[#8C2F12]/10 text-[#8C2F12] font-bold flex items-center justify-center shrink-0">
                {item.step}
              </div>
              <div>
                <h4 className="font-bold text-[#123F13] mb-1">{item.title}</h4>
                <p className="text-sm text-[#3B352F]/70 leading-relaxed">{item.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Hvad I kan få ud af forløbet */}
      <section className="mb-20">
        <div className="text-center max-w-2xl mx-auto mb-12">
          <span className="text-[#8C2F12] font-bold text-xs uppercase tracking-widest block mb-2">Hvad I kan få ud af forløbet</span>
          <h2 className="text-3xl md:text-4xl font-serif text-[#123F13] mb-4">
            Målet er ikke perfekte samtaler.<br className="hidden md:inline" /> Målet er en mere tryg proces.
          </h2>
          <p className="text-[#3B352F]/70 leading-relaxed">
            Et parforhold skal ikke være konfliktfrit for at være sundt. Men konflikter må ikke blive den eneste måde at skabe kontakt på.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {[
            { step: "01", title: "Mere overblik", desc: "I får sprog for det mønster, I sidder fast i, og forstår bedre, hvorfor det er så svært at stoppe, selv når I begge gerne vil." },
            { step: "02", title: "Mindre gentagelse", desc: "I lærer at afbryde de samtaler, der ikke fører videre, og vælge en mere enkel måde at vende tilbage til hinanden på." },
            { step: "03", title: "Konkrete næste skridt", desc: "I får forslag til, hvad I kan øve mellem sessionerne, og hvordan I kan gøre hverdagen lidt lettere at være i." }
          ].map((item, idx) => (
            <div key={idx} className="bg-[#FAF7F2] border border-[#E8DDCE] rounded-2xl p-6 shadow-sm hover:shadow-md transition-all">
              <div className="text-sm font-bold text-[#8C2F12] mb-4">{item.step}</div>
              <h3 className="text-xl font-serif text-[#123F13] mb-3">{item.title}</h3>
              <p className="text-sm text-[#3B352F]/70 leading-relaxed">{item.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Forløbet */}
      <section className="grid grid-cols-1 lg:grid-cols-12 gap-8 mb-20">
        <div className="lg:col-span-7 space-y-8">
          <div>
            <span className="text-[#8C2F12] font-bold text-xs uppercase tracking-widest flex items-center gap-2 mb-2">
              <span className="w-8 h-[1px] bg-[#8C2F12]"></span>
              Forløbet
            </span>
            <h2 className="text-3xl font-serif text-[#123F13] mb-4">Hvordan foregår parterapi hos Core Hypnose?</h2>
            <p className="text-[#3B352F]/70 leading-relaxed">
              Jeg arbejder roligt, struktureret og konkret. I skal ikke præstere. I skal ikke kunne forklare alt perfekt.
              Jeg hjælper jer med at få situationen ned på jorden.
            </p>
          </div>

          <div className="space-y-4">
            {treatmentSteps.map((step, idx) => {
              const title = step.split(":")[0];
              const desc = step.split(":")[1];
              return (
                <div key={idx} className="bg-white border border-[#E8DDCE] rounded-2xl p-6 shadow-sm flex gap-5 items-start">
                  <div className="w-12 h-12 rounded-xl bg-[#123F13] text-white font-serif text-2xl font-bold flex items-center justify-center shrink-0">
                    {idx + 1}
                  </div>
                  <div>
                    <h3 className="text-xl font-serif text-[#123F13] mb-1">{title}</h3>
                    <p className="text-sm text-[#3B352F]/70 leading-relaxed">{desc}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="lg:col-span-5 bg-white border border-[#E8DDCE] rounded-3xl p-8 shadow-sm flex flex-col justify-between min-h-[380px]">
          <div className="space-y-4">
            <span className="text-[#8C2F12] font-bold text-xs uppercase tracking-widest block">Skriftlig plan</span>
            <h3 className="text-2xl font-serif text-[#123F13]">En konkret relationsplan</h3>
            <p className="text-sm text-[#3B352F]/70 leading-relaxed">
              Mange par har talt meget om problemerne, men mangler en klar retning. Derfor arbejder jeg gerne med en skriftlig behandlings- eller relationsplan efter den første session. Planen kan beskrive, hvad I skal være opmærksomme på, hvilke mønstre der skal brydes, og hvilke næste skridt der giver bedst mening.
            </p>
          </div>
          <div className="flex justify-end mt-6">
            <RedWaxSeal className="w-24 h-24 rotate-[-7deg] drop-shadow-[0_12px_20px_rgba(74,20,17,0.18)]" />
          </div>
        </div>
      </section>

      {/* Parterapi eller relationscoaching */}
      <section className="mb-20">
        <div className="text-center max-w-2xl mx-auto mb-12">
          <span className="text-[#8C2F12] font-bold text-xs uppercase tracking-widest block mb-2">Parterapi eller relationscoaching</span>
          <h2 className="text-3xl md:text-4xl font-serif text-[#123F13] mb-4">Hvilken type hjælp passer til jer?</h2>
          <p className="text-[#3B352F]/70 leading-relaxed">
            Nogle har brug for terapi, fordi relationen er præget af smerte, konflikter eller gamle sår. Andre har brug for relationscoaching, fordi de ønsker bedre kommunikation, tydeligere aftaler og mere nærvær.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          <div className="bg-white border border-[#E8DDCE] rounded-3xl p-8 shadow-sm relative overflow-hidden">
            <div className="absolute -right-6 -top-6 w-20 h-24 rounded-full bg-[#8C2F12]/5" />
            <h3 className="text-2xl font-serif text-[#123F13] mb-4">Parterapi</h3>
            <p className="text-sm text-[#3B352F]/70 leading-relaxed mb-6">
              For jer, der oplever gentagne konflikter, brud på tillid, afstand, krise, svære følelser eller tvivl om relationens fremtid.
            </p>
            <ul className="space-y-3 text-sm text-[#3B352F]/80">
              <li className="flex items-start gap-2"><span className="text-[#8C2F12] font-bold">✓</span> Konflikter og fastlåste samtaler</li>
              <li className="flex items-start gap-2"><span className="text-[#8C2F12] font-bold">✓</span> Afstand, tavshed eller følelsesmæssig kulde</li>
              <li className="flex items-start gap-2"><span className="text-[#8C2F12] font-bold">✓</span> Jalousi, tillidsbrud eller utroskab</li>
              <li className="flex items-start gap-2"><span className="text-[#8C2F12] font-bold">✓</span> Bekymring, grubleri og gamle konflikter, der fylder</li>
            </ul>
          </div>

          <div className="bg-white border border-[#E8DDCE] rounded-3xl p-8 shadow-sm relative overflow-hidden">
            <div className="absolute -right-6 -top-6 w-20 h-24 rounded-full bg-[#123F13]/5" />
            <h3 className="text-2xl font-serif text-[#123F13] mb-4">Relationscoaching</h3>
            <p className="text-sm text-[#3B352F]/70 leading-relaxed mb-6">
              For jer, der grundlæggende fungerer, men ønsker bedre kommunikation, mere tydelighed og en sundere måde at håndtere forskelle på.
            </p>
            <ul className="space-y-3 text-sm text-[#3B352F]/80">
              <li className="flex items-start gap-2"><span className="text-[#8C2F12] font-bold">✓</span> Bedre samtaler i hverdagen</li>
              <li className="flex items-start gap-2"><span className="text-[#8C2F12] font-bold">✓</span> Klarere aftaler om tid, ansvar og behov</li>
              <li className="flex items-start gap-2"><span className="text-[#8C2F12] font-bold">✓</span> Styrket samarbejde omkring børn, familie eller arbejde</li>
              <li className="flex items-start gap-2"><span className="text-[#8C2F12] font-bold">✓</span> Forebyggelse før problemerne vokser sig større</li>
            </ul>
          </div>
        </div>
      </section>

      {/* Pris og rammer */}
      <section className="bg-gradient-to-br from-[#FAF7F2] to-[#F4EFE7] border border-[#E8DDCE] rounded-3xl p-8 lg:p-12 shadow-sm mb-20 scroll-mt-24" id="priser">
        <div className="text-center max-w-3xl mx-auto mb-12">
          <span className="text-[#8C2F12] font-bold text-xs uppercase tracking-widest block mb-2">Pris og rammer</span>
          <h2 className="text-3xl md:text-4xl font-serif text-[#123F13] mb-4">Parterapi og relationscoaching <br className="hidden md:inline" />med en rolig indgang</h2>
          <p className="text-[#3B352F]/70 leading-relaxed">
            Den første session er lavet som en afklarende relationssession, så du eller I kan komme i gang uden at binde jer til et helt forløb fra start.
            Herefter er prisstrukturen enkel og afhænger af, om arbejdet fortsætter individuelt eller som par.
          </p>
        </div>

        {/* Start her - Første relationssession */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-stretch mb-8">
          <div className="lg:col-span-8 bg-white border border-[#E8DDCE] rounded-2xl p-6 lg:p-8 shadow-sm flex flex-col md:flex-row gap-6 items-center justify-between">
            <div className="space-y-3">
              <span className="text-[#8C2F12] font-bold text-xs uppercase tracking-widest block">Start her</span>
              <h3 className="text-2xl font-serif text-[#123F13]">Første relationssession</h3>
              <p className="text-sm text-[#3B352F]/70 leading-relaxed max-w-xl">
                Første session handler om at skabe overblik over situationen, forstå de vigtigste mønstre og finde ud af, hvad der vil være det bedste næste skridt.
                Du kan komme alene, hvis din partner ikke deltager, eller I kan komme sammen.
              </p>
            </div>
            <RedWaxSeal className="w-24 h-24 rotate-[-7deg] drop-shadow-[0_14px_22px_rgba(74,20,17,0.18)]" />
          </div>

          <div className="lg:col-span-4 bg-[#123F13] text-white rounded-2xl p-6 lg:p-8 shadow-md flex flex-col justify-center text-center lg:text-left">
            <span className="text-white/80 font-bold text-xs uppercase tracking-widest block mb-1">Første session</span>
            <strong className="text-4xl lg:text-5xl font-serif leading-none mb-2">375 kr.</strong>
            <span className="text-white/90 text-sm font-bold block mb-1">pr. person · 50 minutter</span>
            <span className="text-white/60 text-xs leading-relaxed">Én person: 375 kr. · to personer: 750 kr.</span>
          </div>
        </div>

        {/* Pricing Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <div className="bg-white border-2 border-[#8C2F12]/40 rounded-2xl p-6 shadow-md hover:shadow-lg transition-all flex flex-col justify-between min-h-[320px] relative overflow-hidden">
            <div className="absolute -right-6 -top-6 w-16 h-16 rounded-full bg-[#8C2F12]/5" />
            <div>
              <span className="bg-[#8C2F12]/10 text-[#8C2F12] text-[10px] font-bold tracking-wider uppercase px-2.5 py-1 rounded-full block w-fit mb-4">Afklaring</span>
              <h3 className="text-xl font-serif text-[#123F13] mb-2">Første relationssession</h3>
              <div className="text-3xl font-serif text-[#123F13] font-bold mb-1">375 kr. <span className="text-xs text-[#3B352F]/60 font-sans font-normal">pr. person</span></div>
              <span className="text-xs text-[#3B352F]/50 font-bold block mb-4">50 minutters varighed</span>
              <p className="text-xs text-[#3B352F]/70 leading-relaxed mb-4">Gælder første session, uanset om du kommer alene eller sammen med din partner.</p>
            </div>
            <ul className="space-y-2 text-xs text-[#3B352F]/80 border-t border-[#E8DDCE]/50 pt-4">
              <li className="flex items-start gap-1.5">✓ Afklaring af mønstre</li>
              <li className="flex items-start gap-1.5">✓ Metakognitive greb</li>
              <li className="flex items-start gap-1.5">✓ Skriftlig strategiplan</li>
            </ul>
          </div>

          <div className="bg-white border border-[#E8DDCE] rounded-2xl p-6 shadow-sm hover:shadow-md transition-all flex flex-col justify-between min-h-[320px]">
            <div>
              <span className="bg-[#123F13]/10 text-[#123F13] text-[10px] font-bold tracking-wider uppercase px-2.5 py-1 rounded-full block w-fit mb-4">Standard</span>
              <h3 className="text-xl font-serif text-[#123F13] mb-2">Parterapi / relationscoaching</h3>
              <div className="text-3xl font-serif text-[#123F13] font-bold mb-1">1.950 kr.</div>
              <span className="text-xs text-[#3B352F]/50 font-bold block mb-4">50 minutters varighed</span>
              <p className="text-xs text-[#3B352F]/70 leading-relaxed mb-4">Gælder når begge deltager, og temaet handler om parforhold, kommunikation, konflikter, afstand eller fælles udvikling.</p>
            </div>
            <ul className="space-y-2 text-xs text-[#3B352F]/80 border-t border-[#E8DDCE]/50 pt-4">
              <li className="flex items-start gap-1.5">✓ Struktureret samtale</li>
              <li className="flex items-start gap-1.5">✓ Konkrete greb til hverdagen</li>
              <li className="flex items-start gap-1.5">✓ Fysisk i Valby eller online</li>
            </ul>
          </div>

          <div className="bg-white border border-[#E8DDCE] rounded-2xl p-6 shadow-sm hover:shadow-md transition-all flex flex-col justify-between min-h-[320px]">
            <div>
              <span className="bg-[#123F13]/10 text-[#123F13] text-[10px] font-bold tracking-wider uppercase px-2.5 py-1 rounded-full block w-fit mb-4">Forløb</span>
              <h3 className="text-xl font-serif text-[#123F13] mb-2">5 sessioner</h3>
              <div className="text-3xl font-serif text-[#123F13] font-bold mb-1">9.250 kr.</div>
              <span className="text-xs text-[#3B352F]/50 font-bold block mb-4">50 minutter pr. session</span>
              <p className="text-xs text-[#3B352F]/70 leading-relaxed mb-4">For jer, der ønsker et kortere, samlet forløb med tydelig retning og opfølgning over flere samtaler.</p>
            </div>
            <ul className="space-y-2 text-xs text-[#3B352F]/80 border-t border-[#E8DDCE]/50 pt-4">
              <li className="flex items-start gap-1.5">✓ Giver ro og struktur</li>
              <li className="flex items-start gap-1.5">✓ Velegnet ved fastlåste mønstre</li>
              <li className="flex items-start gap-1.5">✓ Betales inden opstart</li>
            </ul>
          </div>

          <div className="bg-white border border-[#E8DDCE] rounded-2xl p-6 shadow-sm hover:shadow-md transition-all flex flex-col justify-between min-h-[320px]">
            <div>
              <span className="bg-[#123F13]/10 text-[#123F13] text-[10px] font-bold tracking-wider uppercase px-2.5 py-1 rounded-full block w-fit mb-4">Fordybelse</span>
              <h3 className="text-xl font-serif text-[#123F13] mb-2">10 sessioner</h3>
              <div className="text-3xl font-serif text-[#123F13] font-bold mb-1">17.500 kr.</div>
              <span className="text-xs text-[#3B352F]/50 font-bold block mb-4">50 minutter pr. session</span>
              <p className="text-xs text-[#3B352F]/70 leading-relaxed mb-4">For jer, der ønsker et længere og mere sammenhængende forløb med plads til at arbejde dybere med relationens mønstre.</p>
            </div>
            <ul className="space-y-2 text-xs text-[#3B352F]/80 border-t border-[#E8DDCE]/50 pt-4">
              <li className="flex items-start gap-1.5">✓ Velegnet ved tillidsbrud</li>
              <li className="flex items-start gap-1.5">✓ Mere kontinuitet og opfølgning</li>
              <li className="flex items-start gap-1.5">✓ Betales inden opstart</li>
            </ul>
          </div>
        </div>

        {/* Price Logic Info */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="bg-white/60 border border-[#E8DDCE]/80 rounded-2xl p-6">
            <h4 className="font-bold text-[#123F13] mb-2">Hvis du kommer alene</h4>
            <p className="text-xs text-[#3B352F]/70 leading-relaxed">
              Mange starter alene, fordi partneren ikke er klar endnu. Første relationssession koster 375 kr. Hvis arbejdet derefter fortsætter som individuelt behandlingsforløb, gælder den almindelige individuelle pris på 1.800 kr.
            </p>
          </div>
          <div className="bg-white/60 border border-[#E8DDCE]/80 rounded-2xl p-6">
            <h4 className="font-bold text-[#123F13] mb-2">Hvis I kommer sammen</h4>
            <p className="text-xs text-[#3B352F]/70 leading-relaxed">
              Når begge deltager efter første session, gælder prisen for parterapi og relationscoaching: 1.950 kr. pr. session. Det afspejler, at der er mere kompleksitet i rummet, når to mennesker og relationen er i fokus.
            </p>
          </div>
          <div className="bg-white/60 border border-[#E8DDCE]/80 rounded-2xl p-6">
            <h4 className="font-bold text-[#123F13] mb-2">Enkel betaling</h4>
            <p className="text-xs text-[#3B352F]/70 leading-relaxed">
              Betaling sker altid inden sessionen starter. Det gælder både enkeltsessioner, forløb, klippekort og online-sessioner.
            </p>
          </div>
        </div>

        {/* Price Clarity Banner */}
        <div className="bg-gradient-to-r from-[#123F13] to-[#173F35] text-white rounded-2xl p-6 flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-full bg-white/10 flex items-center justify-center text-xl font-bold shrink-0">?</div>
            <div>
              <strong className="block text-lg mb-1">Er du i tvivl om, hvilken pris der gælder?</strong>
              <span className="text-white/70 text-sm leading-relaxed block max-w-xl">
                Den enkle tommelfingerregel er: arbejder du alene videre med relationstemaet, er det individuel terapi. Deltager I begge, er det parterapi eller relationscoaching.
              </span>
            </div>
          </div>
          <Link to="/kontakt" className="bg-white text-[#123F13] font-bold px-6 py-3 rounded-lg hover:bg-[#FAF7F2] transition-colors shrink-0 text-center w-full md:w-auto">
            Spørg mig først
          </Link>
        </div>
      </section>

      {/* Viden og indsigt */}
      <section className="mb-20">
        <div className="max-w-3xl mb-12">
          <span className="text-[#8C2F12] font-bold text-xs uppercase tracking-widest flex items-center gap-2 mb-2">
            <span className="w-8 h-[1px] bg-[#8C2F12]"></span>
            Viden og indsigt
          </span>
          <h2 className="text-3xl md:text-4xl font-serif text-[#123F13] mb-4">
            Derfor kan parforhold føles fastlåste,<br className="hidden md:inline" /> selv når begge gerne vil hinanden
          </h2>
          <p className="text-lg text-[#3B352F]/70 leading-relaxed">
            Når relationer bliver svære, er det almindeligt at tænke: “Hvorfor forstår vi ikke bare hinanden?”
            Men ofte er det ikke viljen, der mangler. Det er metoden.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="bg-white border border-[#E8DDCE] rounded-2xl p-6 shadow-sm">
            <h3 className="text-xl font-serif text-[#123F13] mb-2">Bekymring om fremtiden</h3>
            <p className="text-sm text-[#3B352F]/70 leading-relaxed">
              Bekymring handler ofte om, hvad der kan ske: “Hvad hvis det aldrig ændrer sig?”, “Hvad hvis vi vokser fra hinanden?”
              Når bekymringen får for meget plads, kan den skabe pres, kontrol og uro i relationen.
            </p>
          </div>
          <div className="bg-white border border-[#E8DDCE] rounded-2xl p-6 shadow-sm">
            <h3 className="text-xl font-serif text-[#123F13] mb-2">Grubleri om fortiden</h3>
            <p className="text-sm text-[#3B352F]/70 leading-relaxed">
              Grubleri handler ofte om det, der allerede er sket: “Hvorfor sagde han det?”, “Hvorfor gjorde hun sådan?”
              Det kan være forståeligt, men hvis analysen aldrig stopper, kan den holde såret åbent.
            </p>
          </div>
          <div className="bg-white border border-[#E8DDCE] rounded-2xl p-6 shadow-sm">
            <h3 className="text-xl font-serif text-[#123F13] mb-2">Forsvar og tilbagetrækning</h3>
            <p className="text-sm text-[#3B352F]/70 leading-relaxed">
              Når vi føler os kritiseret eller misforstået, beskytter vi os. Nogle forklarer og forsvarer sig. Andre lukker ned.
              Begge dele kan give mening – og begge dele kan skabe mere afstand.
            </p>
          </div>
          <div className="bg-white border border-[#E8DDCE] rounded-2xl p-6 shadow-sm">
            <h3 className="text-xl font-serif text-[#123F13] mb-2">Kontakt før konklusion</h3>
            <p className="text-sm text-[#3B352F]/70 leading-relaxed">
              Menge par forsøger at finde den rigtige konklusion for hurtigt: Hvem har ret? Hvad betyder det? Hvad skal vi beslutte?
              Ofte er første skridt at genskabe kontakt nok til, at samtalen kan blive tryg.
            </p>
          </div>
        </div>
      </section>

      {/* Typisk mønster */}
      <section className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-20">
        <div className="bg-white border border-[#E8DDCE] rounded-3xl p-8 lg:p-12 shadow-sm flex flex-col justify-center space-y-6">
          <span className="text-[#8C2F12] font-bold text-xs uppercase tracking-widest flex items-center gap-2">
            <span className="w-8 h-[1px] bg-[#8C2F12]"></span>
            Typisk mønster
          </span>
          <h2 className="text-2xl md:text-3xl font-serif text-[#123F13]">Når én vil tale, og én trækker sig</h2>
          <p className="text-[#3B352F]/80 leading-relaxed">
            Det er et af de mest almindelige mønstre i parforhold. Den ene oplever, at samtalen er nødvendig for at skabe tryghed.
            Den anden oplever, at samtalen bliver for meget og forsøger at skabe ro ved at trække sig.
          </p>
          <p className="text-[#3B352F]/80 leading-relaxed">
            Begge forsøger på hver sin måde at beskytte relationen. Men resultatet kan blive, at den ene føler sig afvist, mens den anden føler sig presset.
          </p>
          <p className="text-[#123F13] font-medium leading-relaxed">
            I terapien arbejder vi med at gøre mønstret tydeligt, så I ikke skal kæmpe mod hinanden, men kan begynde at arbejde med processen sammen.
          </p>
        </div>

        <div className="bg-[#123F13] text-white rounded-3xl p-8 lg:p-12 shadow-lg flex flex-col justify-between min-h-[380px] relative overflow-hidden">
          <div className="absolute -right-16 -top-16 w-64 h-64 rounded-full bg-white/5 border border-white/10" />
          <div className="space-y-6 relative z-10">
            <h3 className="text-2xl font-serif text-[#E8DDCE]">Et enkelt spørgsmål, der ofte hjælper</h3>
            <p className="font-serif text-2xl italic text-[#E8DDCE] leading-relaxed border-l-4 border-[#8C2F12] pl-6">
              “Hvad sker der mellem os lige nu – ikke hvem af os har ret?”
            </p>
            <p className="text-white/80 leading-relaxed">
              Når I flytter opmærksomheden fra skyld til proces, bliver det nemmere at opdage, hvad der faktisk foregår.
            </p>
          </div>
          <div className="relative z-10 flex justify-end mt-6">
            <RedWaxSeal className="w-16 h-16 sm:w-20 sm:h-20 rotate-[-6deg] drop-shadow-[0_10px_18px_rgba(0,0,0,0.3)]" />
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="mb-20 scroll-mt-24" id="faq">
        <div className="max-w-3xl mb-12">
          <span className="text-[#8C2F12] font-bold text-xs uppercase tracking-widest flex items-center gap-2 mb-2">
            <span className="w-8 h-[1px] bg-[#8C2F12]"></span>
            Spørgsmål og svar
          </span>
          <h2 className="text-3xl md:text-4xl font-serif text-[#123F13] mb-4">Ofte stillede spørgsmål om parterapi</h2>
          <p className="text-lg text-[#3B352F]/70 leading-relaxed">
            Her finder du svar på de spørgsmål, mange stiller, før de kontakter mig om parterapi eller relationscoaching i Valby.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {[
            { q: "Hvornår giver det mening at søge parterapi?", a: "Det giver mening, når I sidder fast i de samme samtaler, oplever afstand, mistillid, mange konflikter eller er i tvivl om, hvordan I kommer videre. I behøver ikke være i akut krise for at få hjælp." },
            { q: "Skal begge parter ville det lige meget?", a: "Det er en fordel, hvis begge er villige til at møde op og undersøge mønstret. Men det er normalt, at motivationen ikke er helt ens. Første skridt kan være at få en rolig afklaring uden pres." },
            { q: "Kan jeg komme alene, hvis min partner ikke vil med?", a: "Ja. Relationscoaching kan også give mening alene. Første relationssession koster 375 kr., hvis du kommer alene. Hvis forløbet bagefter fortsætter som individuelt behandlingsforløb, gælder den almindelige individuelle pris på 1.800 kr." },
            { q: "Hvad hvis vi skændes under sessionen?", a: "Det er helt normalt, at svære mønstre viser sig i rummet. Min opgave er at holde samtalen rolig, tydelig og konstruktiv, så vi kan bruge det, der sker, til at forstå mønstret bedre." },
            { q: "Arbejder du med utroskab og tillidsbrud?", a: "Ja, hvis begge er indstillet på at arbejde respektfuldt med situationen. Fokus er at skabe overblik over, hvad der er sket, hvad der fastholder smerten, og hvilke næste skridt der er realistiske." },
            { q: "Hvor mange sessioner har vi brug for?", a: "Det afhænger af situationen. Nogle har brug for få samtaler for at få overblik og retning. Andre har brug for et længere forløb. Efter den første session får I et konkret forslag til næste skridt." },
            { q: "Er parterapi det samme som metakognitiv terapi?", a: "Ikke helt. Parterapi handler om relationen mellem jer. Den metakognitive tilgang bidrager med et skarpt fokus på bekymring, grubleri og de tankeprocesser, der kan fastholde konflikter." },
            { q: "Kan hypnose bruges i arbejdet med relationer?", a: "Hypnose kan bruges som et roligt terapeutisk supplement, hvis det passer til situationen. Det handler om fokuseret opmærksomhed, guidning og nye forslag til reaktionsmønstre – ikke om mystik eller kontrol." },
            { q: "Tilbyder du parterapi online?", a: "Ja. Sessioner kan foregå online eller fysisk i Valby. For nogle par er online en praktisk løsning, især hvis kalender, transport eller afstand gør det svært at mødes fysisk." },
            { q: "Hvad koster den første relationssession?", a: "Første relationssession koster 375 kr. pr. person og varer 50 minutter. Det betyder 375 kr., hvis du kommer alene, og 750 kr., hvis I kommer to personer. Den pris gælder kun den første session." },
            { q: "Hvad koster parterapi og relationscoaching efter første session?", a: "Parterapi og relationscoaching koster 1.950 kr. for 50 minutter, når begge deltager. Et forløb på 5 sessioner koster 9.250 kr., og et forløb på 10 sessioner koster 17.500 kr." },
            { q: "Hvad hvis vi starter sammen, men jeg fortsætter alene?", a: "Hvis forløbet efter første session fortsætter som individuelt behandlingsforløb, gælder priserne for individuel terapi. En almindelig individuel session koster 1.800 kr. for 50 minutter." },
            { q: "Hvordan betaler jeg?", a: "Betaling sker altid inden sessionen starter. Det gælder enkeltsessioner, forløb, klippekort og online-sessioner." },
            { q: "Hvor ligger Core Hypnose?", a: "Core Hypnose ligger på Valby Tingsted 7, 2500 Valby. Der er også mulighed for online-sessioner." }
          ].map((item, idx) => (
            <AccordionItem
              key={idx}
              title={item.q}
              isOpen={openItems.includes(idx)}
              onClick={() => toggleItem(idx)}
            >
              {item.a}
            </AccordionItem>
          ))}
        </div>
      </section>

      {/* CTA Band */}
      <section className="bg-gradient-to-br from-[#123F13] to-[#173F35] text-white rounded-3xl p-8 lg:p-12 shadow-lg mb-20 relative overflow-hidden" id="kontakt">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center relative z-10">
          <div className="lg:col-span-7 space-y-6">
            <RedWaxSeal className="w-24 h-24 rotate-[8deg] drop-shadow-[0_16px_24px_rgba(0,0,0,0.24)]" />
            <h2 className="text-3xl md:text-4xl font-serif leading-tight">Start med en rolig afklaring</h2>
            <p className="text-white/80 leading-relaxed max-w-xl">
              I behøver ikke have den perfekte forklaring klar. Første relationssession koster 375 kr. pr. person og handler om at skabe overblik, forstå mønstret og finde ud af, hvad der vil være et meningsfuldt næste skridt.
            </p>
          </div>

          <div className="lg:col-span-5 bg-white/10 border border-white/20 rounded-2xl p-6 lg:p-8 space-y-4">
            <h3 className="text-xl font-serif text-[#E8DDCE]">Parterapi og relationscoaching</h3>
            <div className="space-y-2 text-sm text-white/80">
              <p className="flex items-center gap-2">✓ Fysisk i Valby eller online</p>
              <p className="flex items-center gap-2">✓ Valby Tingsted 7, 2500 Valby</p>
              <p className="flex items-center gap-2">✓ Mandag-fredag 09.00-16.00</p>
            </div>
            <div className="pt-4">
              <Link to="/bestil-tid" className="bg-white text-[#123F13] font-bold px-6 py-3 rounded-lg hover:bg-[#FAF7F2] transition-colors block text-center w-full shadow-md">
                Book første relationssession
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Schema */}
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": [
              {
                "@type": "Question",
                "name": "Hvornår giver det mening at søge parterapi?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Det giver mening, når I sidder fast i de samme samtaler, oplever afstand, mistillid, mange konflikter eller er i tvivl om, hvordan I kommer videre. I behøver ikke være i akut krise for at få hjælp."
                }
              },
              {
                "@type": "Question",
                "name": "Kan jeg komme alene, hvis min partner ikke vil med?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Ja. Relationscoaching kan også give mening alene. Første relationssession koster 375 kr., hvis du kommer alene. Hvis forløbet bagefter fortsætter som individuelt behandlingsforløb, gælder den almindelige individuelle pris på 1.800 kr."
                }
              },
              {
                "@type": "Question",
                "name": "Tilbyder Core Hypnose parterapi online?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Ja. Sessioner kan foregå online eller fysisk i Valby. For nogle par er online en praktisk løsning, hvis kalender, transport eller afstand gør det svært at mødes fysisk."
                }
              },
              {
                "@type": "Question",
                "name": "Hvad koster den første relationssession?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Første relationssession koster 375 kr. pr. person og varer 50 minutter. Det betyder 375 kr., hvis du kommer alene, og 750 kr., hvis I kommer to personer. Den pris gælder kun den første session."
                }
              },
              {
                "@type": "Question",
                "name": "Hvad koster parterapi og relationscoaching efter første session?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Parterapi og relationscoaching koster 1.950 kr. for 50 minutter, når begge deltager. Et forløb på 5 sessioner koster 9.250 kr., og et forløb på 10 sessioner koster 17.500 kr."
                }
              },
              {
                "@type": "Question",
                "name": "Hvad hvis vi starter sammen, men jeg fortsætter alene?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Hvis forløbet efter første session fortsætter som individuelt behandlingsforløb, gælder priserne for individuel terapi. En almindelig individuel session koster 1.800 kr. for 50 minutter."
                }
              },
              {
                "@type": "Question",
                "name": "Hvor ligger Core Hypnose?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Core Hypnose ligger på Valby Tingsted 7, 2500 Valby. Der er også mulighed for online-sessioner."
                }
              }
            ]
          })}
        </script>
      </Helmet>
    </ServiceLayout>
  );
};

export default Parterapi;
